import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { setLeadView, setChannelTab } from '../../../features/ui/uiSlice';
import { useStages, useOptionList } from '../../../features/options/useOptions';
import {
  useLeadsQuery, useLeadsSummaryQuery, useCreateLeadMutation, useUpdateLeadMutation,
  useSetLeadStageMutation, useDeleteLeadMutation, useConvertLeadMutation, useUsersQuery,
} from '../../../services/api';
import { CHMAP, CHCOLOR } from '../../../constants/channels';
import { inr, inrShort, fmtPct, personSub } from '../../../utils/format';
import { KpiGrid } from '../../../components/common/KpiCard';
import TableCard from '../../../components/common/TableCard';
import SegmentedTabs from '../../../components/common/SegmentedTabs';
import AppButton from '../../../components/common/AppButton';
import DataCard from '../../../components/common/DataCard';
import Pagination from '../../../components/common/Pagination';
import QueryState from '../../../components/common/QueryState';
import { KpiSkeleton, TableSkeleton, BoardSkeleton } from '../../../components/common/Shimmer';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { useToast } from '../../../components/common/Toast';
import { ReadOnlyNotice } from '../../../components/common/Access';
import { usePermissions } from '../../../features/auth/permissions';
import { CONTROL_SX, PillSelect } from '../../../components/common/FormField';
import { SectionHeading, TagChip, ConditionChip, OwnerAvatar, FootNote } from '../../../components/common/Bits';
import LeadCard, { StageSelect } from './LeadCard';
import LeadModal from './LeadModal';

/** The board shows whole columns at once, the table is read a page at a time. */
const BOARD_LIMIT = 200;
const LIST_LIMIT = 12;

function LeadsBoard({ channelId, leads, stages, handlers, canEdit }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1.5 }}>
      {stages.map((s) => {
        const items = leads.filter((l) => l.stage === s.k);
        const sum = items.reduce((t, l) => t + (+l.value || 0), 0);
        return (
          <Box
            key={s.k}
            sx={{
              flex: '0 0 238px',
              background: 'var(--card-2)',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              p: '10px',
              display: 'flex',
              flexDirection: 'column',
              gap: '9px',
              minHeight: 140,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.05em',
                textTransform: 'uppercase', color: 'var(--ink-2)', p: '0 2px 2px',
              }}
            >
              <span>{s.k}</span>
              <Box component="span" sx={{ color: 'var(--muted)' }}>{items.length} · {inrShort(sum)}</Box>
            </Box>
            {items.length === 0 ? (
              <Box sx={{ color: 'var(--muted)', fontSize: 12, textAlign: 'center', p: '12px 0' }}>—</Box>
            ) : (
              items.map((l) => (
                <LeadCard
                  key={l.id}
                  channelId={channelId}
                  lead={l}
                  stages={stages}
                  onStage={(v) => handlers.stage(l.id, v)}
                  onEdit={() => handlers.edit(l)}
                  onDelete={() => handlers.remove(l)}
                  onConvert={() => handlers.convert(l)}
                  canEdit={canEdit}
                />
              ))
            )}
          </Box>
        );
      })}
    </Box>
  );
}

function LeadsList({ channelId, leads, stages, handlers, canEdit }) {
  const isD2C = channelId === 'd2c';
  const order = Object.fromEntries(stages.map((s, i) => [s.k, i]));
  const rows = leads
    .slice()
    .sort((a, b) => (order[a.stage] - order[b.stage]) || (+b.value || 0) - (+a.value || 0));

  if (!rows.length) return <DataCard>No leads match. Click “Add Lead” to create one.</DataCard>;

  return (
    <TableCard>
      <table className="tbl">
        <thead>
          <tr>
            <th>{isD2C ? 'Patient' : 'Lead / Organization'}</th>
            <th>{isD2C ? 'Gender · Age' : 'Contact'}</th>
            <th>Phone</th>
            <th>Location</th>
            {isD2C && <th>Known conditions</th>}
            <th>Source</th>
            <th>Owner</th>
            <th className="num">Est. value</th>
            <th>Stage</th>
            {canEdit && <th style={{ textAlign: 'right' }}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((l) => {
            const loc = [l.city, l.state].filter(Boolean).join(', ');
            return (
              <tr key={l.id}>
                <td><b>{l.name}</b></td>
                <td style={{ color: 'var(--ink-2)' }}>{(isD2C ? personSub(l) : l.contact) || '—'}</td>
                <td className="code">{l.phone || '—'}</td>
                <td style={{ color: 'var(--ink-2)' }}>
                  {loc || '—'} {l.pincode && <span className="code">{l.pincode}</span>}
                </td>
                {isD2C && (
                  <td>
                    {l.conditions?.length ? l.conditions.map((c) => <ConditionChip key={c}>{c}</ConditionChip>) : '—'}
                  </td>
                )}
                <td><TagChip>{l.source || '—'}</TagChip></td>
                <td>
                  <OwnerAvatar name={l.owner} />{' '}
                  <span style={{ color: 'var(--ink-2)', fontSize: 12 }}>{l.owner || '—'}</span>
                </td>
                <td className="num"><b>{inr(l.value || 0)}</b></td>
                <td>
                  {canEdit ? (
                    <StageSelect showBorder value={l.stage} stages={stages} onChange={(v) => handlers.stage(l.id, v)} />
                  ) : (
                    <TagChip>{l.stage}</TagChip>
                  )}
                </td>
                {canEdit && (
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {l.stage === 'Won' && (
                      <AppButton mini tone="gold" sx={{ mr: 0.5 }} onClick={() => handlers.convert(l)}>→ Account</AppButton>
                    )}
                    <AppButton mini onClick={() => handlers.edit(l)}>Edit</AppButton>{' '}
                    <AppButton mini tone="danger" onClick={() => handlers.remove(l)}>✕</AppButton>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </TableCard>
  );
}

export default function LeadsTab({ channelId }) {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const leadView = useAppSelector((s) => s.ui.leadView);
  const stages = useStages();
  const { canEdit, role } = usePermissions();
  const sources = useOptionList('sources');
  const { data: team } = useUsersQuery({ active: 'true', limit: 200 });
  const owners = (team?.items || []).map((u) => u.name);

  const [search, setSearch] = useState('');
  const [stage, setStage] = useState('');
  const [owner, setOwner] = useState('');
  const [source, setSource] = useState('');
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState({ open: false, lead: null });
  const [confirm, setConfirm] = useState(null);

  // Debounced so typing a name is one request, not one per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setTerm(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Switching channel or view starts a fresh read — a page 4 in one channel is
  // meaningless in the next.
  useEffect(() => { setPage(1); }, [stage, owner, source]);

  // switching channel or view clears the filters too — a stage from another
  // pipeline, or an owner who doesn't work this channel, would just show nothing
  useEffect(() => {
    setPage(1);
    setStage('');
    setOwner('');
    setSource('');
  }, [channelId, leadView]);

  const listArgs = useMemo(
    () => ({
      channel: channelId,
      search: term || undefined,
      stage: stage || undefined,
      owner: owner || undefined,
      source: source || undefined,
      page: leadView === 'board' ? 1 : page,
      limit: leadView === 'board' ? BOARD_LIMIT : LIST_LIMIT,
    }),
    [channelId, term, stage, owner, source, page, leadView]
  );

  const listQuery = useLeadsQuery(listArgs);
  const summaryQuery = useLeadsSummaryQuery({ channel: channelId });

  const [createLead, createState] = useCreateLeadMutation();
  const [updateLead, updateState] = useUpdateLeadMutation();
  const [setLeadStage] = useSetLeadStageMutation();
  const [deleteLead] = useDeleteLeadMutation();
  const [convertLead] = useConvertLeadMutation();
  const saving = createState.isLoading || updateState.isLoading;

  const filtered = Boolean(term || stage || owner || source);
  const clearFilters = () => { setSearch(''); setStage(''); setOwner(''); setSource(''); };

  const fail = (e, fallback) => toast(e?.data?.error || e?.message || fallback, 'error');

  const handlers = {
    stage: async (id, stage) => {
      try {
        await setLeadStage({ id, stage, listArgs }).unwrap();
      } catch (e) {
        fail(e, "Couldn't move that lead");
      }
    },
    edit: (lead) => setModal({ open: true, lead }),
    remove: (lead) =>
      setConfirm({
        tone: 'danger',
        title: 'Remove lead',
        message: `Remove “${lead.name}” from the pipeline?`,
        detail: 'This cannot be undone.',
        confirmLabel: 'Remove',
        run: async () => {
          await deleteLead(lead.id).unwrap();
          toast('Lead removed');
        },
        fallback: "Couldn't remove that lead",
      }),
    convert: (lead) =>
      setConfirm({
        tone: 'brand',
        title: 'Convert to account',
        icon: '★',
        message: `Convert “${lead.name}” into an account?`,
        detail: 'It moves out of Leads and into Accounts.',
        confirmLabel: 'Convert',
        run: async () => {
          await convertLead(lead.id).unwrap();
          toast(`“${lead.name}” is now an account`);
          dispatch(setChannelTab('accounts'));
        },
        fallback: "Couldn't convert that lead",
      }),
  };

  const runConfirm = async () => {
    const c = confirm;
    setConfirm(null);
    try {
      await c.run();
    } catch (e) {
      fail(e, c.fallback);
    }
  };

  const save = async (data) => {
    try {
      if (modal.lead) {
        await updateLead({ id: modal.lead.id, ...data }).unwrap();
        toast('Lead updated');
      } else {
        await createLead({ channel: channelId, ...data }).unwrap();
        toast('Lead added');
      }
      setModal({ open: false, lead: null });
    } catch (e) {
      fail(e, "Couldn't save that lead");
    }
  };

  const s = summaryQuery.data;

  return (
    <>
      {!canEdit && <ReadOnlyNotice role={role} what="the pipeline" />}
      {s ? (
        <KpiGrid
          columns={4}
          items={[
            { label: 'Total Leads', value: s.total, sub: `${s.open} open · ${s.won} won`, accent: CHCOLOR[channelId] },
            { label: 'Open Pipeline', value: inrShort(s.openValue), sub: 'Est. monthly value', accent: 'var(--s4)' },
            { label: 'Won Value', value: inrShort(s.wonValue), sub: `${s.won} deals closed`, accent: 'var(--good)' },
            { label: 'Win Rate', value: fmtPct(s.winRate, 0), sub: `${s.won} won / ${s.lost} lost`, accent: 'var(--s3)' },
          ]}
        />
      ) : (
        <KpiSkeleton columns={4} />
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', m: '6px 0 12px', flexWrap: 'wrap', gap: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <SectionHeading sx={{ m: 0 }}>Pipeline · {CHMAP[channelId].name}</SectionHeading>
          <SegmentedTabs
            dense
            value={leadView}
            onChange={(v) => dispatch(setLeadView(v))}
            items={[{ value: 'board', label: '▦ Board' }, { value: 'list', label: '☰ List' }]}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          <InputBase
            sx={{ ...CONTROL_SX, width: { xs: '100%', sm: 200 } }}
            placeholder="Search name, contact, phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <PillSelect
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            options={[{ value: '', label: 'Any stage' }, ...stages.map((s) => s.k)]}
            sx={{ minWidth: 132 }}
          />
          <PillSelect
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            options={[{ value: '', label: 'Any owner' }, ...owners]}
            sx={{ minWidth: 132 }}
          />
          <PillSelect
            value={source}
            onChange={(e) => setSource(e.target.value)}
            options={[{ value: '', label: 'Any source' }, ...sources]}
            sx={{ minWidth: 132 }}
          />
          {filtered && (
            <AppButton mini onClick={clearFilters}>✕ Clear</AppButton>
          )}
          {canEdit && (
            <AppButton tone="gold" onClick={() => setModal({ open: true, lead: null })}>＋ Add Lead</AppButton>
          )}
        </Box>
      </Box>

      <QueryState
        query={listQuery}
        skeleton={leadView === 'board' ? <BoardSkeleton columns={7} /> : <TableSkeleton columns={channelId === 'd2c' ? 10 : 9} rows={8} />}
      >
        {(data) => (
          <>
            {leadView === 'board' ? (
              <LeadsBoard channelId={channelId} leads={data.items} stages={stages} handlers={handlers} canEdit={canEdit} />
            ) : (
              <>
                <LeadsList channelId={channelId} leads={data.items} stages={stages} handlers={handlers} canEdit={canEdit} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5, gap: 1, flexWrap: 'wrap' }}>
                  <Box sx={{ fontSize: 12, color: 'var(--muted)' }}>
                    {data.total} lead{data.total === 1 ? '' : 's'}
                    {term ? ` matching “${term}”` : ''}
                  </Box>
                  <Pagination page={data.page} pages={data.pages} onChange={setPage} />
                </Box>
              </>
            )}

            <FootNote>
              {leadView === 'board'
                ? `Change a lead's stage with the dropdown on its card. Showing the newest ${Math.min(data.total, BOARD_LIMIT)} of ${data.total}${data.total > BOARD_LIMIT ? ' — switch to List to page through the rest' : ''}.`
                : 'Change stage inline in the table.'}
            </FootNote>
          </>
        )}
      </QueryState>

      <ConfirmDialog
        open={!!confirm}
        tone={confirm?.tone}
        icon={confirm?.icon}
        title={confirm?.title}
        message={confirm?.message}
        detail={confirm?.detail}
        confirmLabel={confirm?.confirmLabel}
        onCancel={() => setConfirm(null)}
        onConfirm={runConfirm}
      />

      <LeadModal
        open={modal.open}
        channelId={channelId}
        lead={modal.lead}
        saving={saving}
        onClose={() => setModal({ open: false, lead: null })}
        onSave={save}
      />
    </>
  );
}
